
<template>
  <div id="app">
    <!-- Loading state while checking authentication -->
    <div v-if="isCheckingAuth" class="auth-loading">
      <div class="loading-spinner"></div>
      <p>Loading...</p>
    </div>
    
    <!-- CRITICAL FIX: Show router-view for password change route even if not authenticated -->
    <div v-else-if="$route.path === '/change-password'">
      <router-view />
    </div>
    
    <!-- Show login form if not authenticated -->
    <AuthForm 
      v-else-if="!isAuthenticated" 
      @authenticated="handleAuthentication"
    />
    
    <!-- Show main app if authenticated -->
    <div v-else class="main-app">
      <!-- User info bar -->
     
<div class="user-bar">
  <div class="user-info">
    <span class="welcome-text">Welcome, {{ userInfo.firstName || userInfo.email }}</span>
    <span v-if="isAdmin" class="admin-badge">
      <i class="fas fa-crown"></i> Admin
    </span>
  </div>
  <div class="nav-actions">
    <!-- ADD: Agent Notifications (only for agents) -->
    <AgentNotifications 
      v-if="userRole === 'agent' || userRole === 'client'" 
      @show-notification="showNotification" 
    />
    
    <router-link to="/" class="nav-link">
      <i class="fas fa-search"></i>
      Search
    </router-link>
  
    <!--metrics -->
    <router-link 
      v-if="!isAdmin && userRole === 'agent'"
      to="/candidate-pool" 
      class="nav-link"
    >
      <i class="fas fa-users"></i>
      Candidates Metrics
    </router-link>
    <!-- <router-link v-if="userRole === 'agent'" to="/history" class="nav-link">
      <i class="fas fa-history"></i> Espace Perso.
    </router-link> -->
    
    <router-link v-if="isAdmin" to="/admin" class="nav-link admin-nav">
      <i class="fas fa-chart-bar"></i>
      Admin Dashboard
    </router-link>
    
    <!-- Theme Toggle Button - Professional Style -->
    <button @click="toggleTheme" class="theme-toggle-btn" :title="currentTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
      <i :class="currentTheme === 'dark' ? 'fas fa-sun' : 'fas fa-moon'"></i>
    </button>
    
    <button @click="handleSignOut" class="sign-out-btn">
      Sign Out
    </button>
  </div>
</div>
 <!-- Main application -->
 <router-view />
</div>
</div>
</template>


<script>
import AuthForm from './components/AuthForm.vue';
import authService from './services/auth.js';
import axios from 'axios';
import AgentNotifications from './components/AgentNotifications.vue';


export default {
  name: "App",
  components: {
    AuthForm,
    AgentNotifications
  },
  
  data() {
    return {
      currentTheme: 'dark', // Default theme
      userRole: null,  

      isAuthenticated: false,
      isCheckingAuth: true,
      isAdmin: false,
      userInfo: {
        email: '',
        firstName: '',
        lastName: ''
      }
    };
  },
  
  async mounted() {
    try {
    const response = await axios.get('/api/v1/clients/user/type');
    this.userRole = response.data.user_type;
  } catch (error) {
    this.userRole = 'agent'; // Default
  }
    await this.checkAuthenticationState();
    const savedTheme = localStorage.getItem('theme') || 'dark';
    this.setTheme(savedTheme);
  },
  
  methods: {
    showNotification(message, type = 'info') {
    // Simple console notification for now - you can enhance this
    console.log(`${type.toUpperCase()}: ${message}`);
    },
    toggleTheme() {
      const newTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
      this.setTheme(newTheme);
    },
    
    setTheme(theme) {
      this.currentTheme = theme;
      
      // Set the data-theme attribute on document root
      document.documentElement.setAttribute('data-theme', theme);
      
      // Save to localStorage
      localStorage.setItem('theme', theme);
      
      console.log('🎨 Theme changed to:', theme);
    },
    async checkAuthenticationState() {
      this.isCheckingAuth = true;
      
      try {
        const user = await authService.getCurrentUser();
        
        if (user) {
          this.isAuthenticated = true;
          this.userInfo = {
            email: user.attributes?.email || '',
            firstName: user.attributes?.given_name || '',
            lastName: user.attributes?.family_name || ''
          };
          
          // Check admin status
          await this.checkAdminStatus();
          
          console.log('✅ User authenticated:', this.userInfo.email);
        } else {
          this.isAuthenticated = false;
          console.log('❌ User not authenticated');
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        this.isAuthenticated = false;
      }
      
      this.isCheckingAuth = false;
    },
    
    async checkAdminStatus() {
      try {
        // Try to access admin endpoint to check if user is admin
        await axios.get('/api/v1/admin/kpi/overview');
        this.isAdmin = true;
        console.log('✅ Admin access confirmed');
      } catch (error) {
        this.isAdmin = false;
        // Don't log admin check failures as they're expected for non-admin users
      }
    },
    
    async handleAuthentication() {
      // Re-check authentication state after successful login
      await this.checkAuthenticationState();
    },
    
    async handleSignOut() {
      try {
         //  Clear project context before signing out
        const { useProjectStore } = await import('@/store/projectContext')
        const projectStore = useProjectStore()
        projectStore.clearProject()

        // ALSO clear legacy localStorage key
        localStorage.removeItem('selectedProject')
        
        await authService.signOut();
        this.isAuthenticated = false;
        this.isAdmin = false;
        this.userInfo = { email: '', firstName: '', lastName: '' };
        console.log('✅ User signed out');
      } catch (error) {
        console.error('Sign out failed:', error);
      }
    }
  },
  watch: {
    // Watch for route changes and refresh auth state when going to home
    '$route'(to, from) {
      console.log('🔄 Route changed from', from.path, 'to', to.path);
      
      // If user navigated from /change-password to home, refresh auth state
      if (from.path === '/change-password' && to.path === '/') {
        console.log('🔄 User completed password change, refreshing authentication state...');
        this.checkAuthenticationState();
      }
    }
  }
};
</script>

<style>
/* Keep your existing global styles */
/* Force all text to use theme colors */
:root {
  font-size: 13px;  
}
/* Normalize form controls */
button,
input,
select,
textarea {
  font-size: 1rem;       /* tie back to root */
  line-height: 1.4;      /* adjust as needed */
}

/* Placeholder text */
::placeholder {
  font-size: 0.9rem;     /* slightly smaller than body text */
  opacity: 0.7;          /* optional: makes it feel lighter */
}

/* Font Awesome icons */
.fa, .fas, .far, .fal, .fad, .fab {
  font-size: 0.9em;      /* scale icons down slightly */
  line-height: 1;        /* prevents them from stretching button height */
  vertical-align: middle;
}
/* Light theme variables */
:root[data-theme="light"] {
  --primary-color: #d42f3d;
  --background-color: #faf9f6;
  --text-color: #2a2a28;
  --surface-color: #ede9e4;
  --border-color: rgba(42, 42, 40, 0.08);
  --hover-color: rgba(212, 47, 61, 0.1);
}

/* Dark theme variables (default) */
:root[data-theme="dark"],
:root {
  --primary-color: #d42f3d;
  --background-color: #262624;
  --text-color: #FAF9F6;
  --surface-color: #2e2e2a;
  --border-color: rgba(250, 249, 246, 0.1);
  --hover-color: rgba(212, 47, 61, 0.1);
}
/* Nuclear option - only for light theme */
 /* Light theme - force text color on text elements only */
:root[data-theme="light"] p,
:root[data-theme="light"] div:not([class*="icon"]):not([class*="btn"]):not([class*="badge"]),
:root[data-theme="light"] h1,
:root[data-theme="light"] h2,
:root[data-theme="light"] h3,
:root[data-theme="light"] h4,
:root[data-theme="light"] h5,
:root[data-theme="light"] h6,
:root[data-theme="light"] label,
:root[data-theme="light"] .candidate-name,
:root[data-theme="light"] .candidate-title,
:root[data-theme="light"] .location,
:root[data-theme="light"] .at,
:root[data-theme="light"] .jobby {
  color: #2a2a28 !important;
}
 /* Exception: Keep multiselect dropdown text as is */
:root[data-theme="light"] .p-multiselect-item,
:root[data-theme="light"] .p-multiselect-label,
:root[data-theme="light"] .p-multiselect-placeholder,
:root[data-theme="light"] .p-multiselect-filter {
  color: #2a2a28 !important; /* This maintains the dark text for light theme multiselect */
}

* {
  box-sizing: border-box;
}

body {
   margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  background-color: var(--background-color);
  color: var(--text-color);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Authentication loading screen */
.auth-loading {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--background-color);
  color: var(--text-color);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-color);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* User bar styles */
 
 


.user-bar {
  background: var(--surface-color);
  border-bottom: 1px solid var(--border-color);
  padding: 0.4rem 1.5rem; /* Reduced from 0.75rem 2rem */
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.6rem; /* Reduced from 1rem */
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem; /* Reduced from 1rem */
}

.welcome-text {
  color: var(--text-color);
  font-size: 0.8rem; /* Reduced from 0.9rem */
}

.nav-link {
  color: var(--text-color);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.3rem; /* Reduced from 0.5rem */
  padding: 0.3rem 0.6rem; /* Reduced from 0.5rem 1rem */
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  font-size: 0.8rem; /* Reduced from 0.9rem */
}

.admin-badge {
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  color: #1a1a1a;
  padding: 0.15rem 0.35rem; /* Reduced from 0.25rem 0.5rem */
  border-radius: 1rem;
  font-size: 0.65rem; /* Reduced from 0.75rem */
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.15rem; /* Reduced from 0.25rem */
  box-shadow: 0 2px 4px rgba(255, 215, 0, 0.3);
}

.sign-out-btn {
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-color);
  padding: 0.3rem 0.6rem; /* Reduced from 0.5rem 1rem */
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8rem; /* Reduced from 0.9rem */
}

 

.nav-link {
  color: var(--text-color);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.nav-link:hover {
  background: var(--hover-color);
  color: var(--primary-color);
}

.nav-link.router-link-active {
  background: rgba(212, 47, 61, 0.1);
  color: var(--primary-color);
  border: 1px solid rgba(212, 47, 61, 0.3);
}

.nav-link.admin-nav {
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
}

.nav-link.admin-nav:hover {
  background: rgba(255, 215, 0, 0.2);
  color: #ffd700;
}

.nav-link.admin-nav.router-link-active {
  background: rgba(255, 215, 0, 0.2);
  color: #ffd700;
  border: 1px solid rgba(255, 215, 0, 0.5);
}
  
.sign-out-btn:hover {
  background: var(--hover-color);
  border-color: var(--primary-color);
  color: var(--primary-color);
}
/* Main app container */
.main-app {
  min-height: 100vh;
  background: var(--background-color);
}

/* Keep all your existing styles below this */
.container {
  max-width:70%;
  margin: 0 auto;
  padding: 0 2rem;
}

.action-button {
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  color: var(--text-color);
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;
}

.action-button:hover {
  background: var(--hover-color);
  border-color: var(--primary-color);
}

.action-button.primary {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.action-button.primary:hover {
  background: #b52835;
}

.medium-text {
  font-size: 1.125rem !important;
}

.small-text {
  font-size: 0.875rem !important;
}

.align-vertical-center {
  display: flex;
  align-items: center;
}

.align-space-between {
  display: flex;
  justify-content: space-between;
}

.align-horizontal {
  display: flex;
}

input, textarea, select {
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  color: var(--text-color);
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

input:focus, textarea:focus, select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(212, 47, 61, 0.1);
}

@media screen and (max-width: 1100px) {
  .container {
    padding: 0 1rem;
  }
  
  .medium-text {
    font-size: 1.125rem !important;
  }
  
  .small-text {
    font-size: 0.875rem !important;
  }
  
  .user-bar {
    padding: 0.75rem 1rem;
  }
}

 




/* Theme toggle button */
.theme-toggle {
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 3rem;
  height: 3rem;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1000;
  color: var(--text-color);
}

.theme-toggle:hover {
  background: var(--hover-color);
  border-color: var(--primary-color);
  transform: scale(1.05);
}

/* Rest of your existing styles */
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  background-color: var(--background-color);
  color: var(--text-color);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.main-app {
  min-height: 100vh;
  background: var(--background-color);
  transition: background-color 0.3s ease;
}

.container {
  max-width: 90%;
  margin: 0 auto;
  padding: 0 1rem;
}

.action-button {
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  color: var(--text-color);
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;
}

.action-button:hover {
  background: var(--hover-color);
  border-color: var(--primary-color);
}

.action-button.primary {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.action-button.primary:hover {
  background: #b52835;
}

.medium-text {
  font-size: 1.125rem !important;
}

.small-text {
  font-size: 0.875rem !important;
}

.align-vertical-center {
  display: flex;
  align-items: center;
}

.align-space-between {
  display: flex;
  justify-content: space-between;
}

.align-horizontal {
  display: flex;
}

input, textarea, select {
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  color: var(--text-color);
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

input:focus, textarea:focus, select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(212, 47, 61, 0.1);
}

@media screen and (max-width: 1100px) {
  .container {
    padding: 0 1rem;
  }
  
  .medium-text {
    font-size: 1.125rem !important;
  }
  
  .small-text {
    font-size: 0.875rem !important;
  }
}
/* Light theme - black text */
:root[data-theme="light"] .candidate-name {
  color: #2a2a28 !important;
}

:root[data-theme="light"] .candidate-title {
  color: #2a2a28 !important;
}


:root[data-theme="light"] .more-skills {
  color: rgba(42, 42, 40, 0.7) !important;
}:root[data-theme="light"] .checkbox-description {
  color: rgba(42, 42, 40, 0.7) !important;
}
 
:root[data-theme="light"] .simple-page-indicator {
  color: rgba(42, 42, 40, 0.7) !important;
}
:root[data-theme="light"] .responsibilities li {
  color: rgba(42, 42, 40, 0.7) !important;
}:root[data-theme="light"] .at {
  color: rgba(42, 42, 40, 0.7) !important;
}

:root[data-theme="light"] .jobby {
  color: rgba(42, 42, 40, 0.6) !important;
}

:root[data-theme="light"] .location {
  color: rgba(42, 42, 40, 0.5) !important;
}

:root[data-theme="light"] .candidate-id, 
:root[data-theme="light"] .indexed-date {
  color: rgba(42, 42, 40, 0.6) !important;
}

:root[data-theme="light"] .match-text {
  color: rgba(42, 42, 40, 0.9) !important;
}

:root[data-theme="light"] .match-type-heading {
  color: rgba(42, 42, 40, 0.9) !important;
}

/* Dark theme keeps original colors (white text) */
:root[data-theme="dark"] .candidate-name {
  color: #ffffff !important;
}

:root[data-theme="dark"] .candidate-title {
  color: rgba(255, 255, 255, 0.85) !important;
}

:root[data-theme="dark"] .at {
  color: rgba(250, 249, 246, 0.694) !important;
}

:root[data-theme="dark"] .jobby {
  color: rgba(255, 255, 255, 0.6) !important;
}

:root[data-theme="dark"] .location {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Light theme - dark text */
:root[data-theme="light"] .candidate-name {
  color: #2a2a28 !important;
}

:root[data-theme="light"] .candidate-title {
  color: rgba(42, 42, 40, 0.85) !important;
}

:root[data-theme="light"] .at {
  color: rgba(42, 42, 40, 0.694) !important;
}

:root[data-theme="light"] .jobby {
  color: rgba(42, 42, 40, 0.6) !important;
}

:root[data-theme="light"] .location {
  color: rgba(42, 42, 40, 0.5) !important;
}

:root[data-theme="light"] .candidate-id, 
:root[data-theme="light"] .indexed-date {
  color: rgba(42, 42, 40, 0.6) !important;
}

:root[data-theme="light"] .match-text {
  color: rgba(42, 42, 40, 0.9) !important;
}

:root[data-theme="light"] .match-type-heading {
  color: rgba(42, 42, 40, 0.9) !important;
}

:root[data-theme="light"] .match-type, 
:root[data-theme="light"] .match-context {
  color: rgba(42, 42, 40, 0.6) !important;
}

:root[data-theme="light"] .tag-group-title {
  color: #2a2a28 !important;
}

:root[data-theme="light"] .section-label {
  color: rgba(42, 42, 40, 0.8) !important;
}

:root[data-theme="light"] .timeline-agent {
  color: rgba(42, 42, 40, 0.6) !important;
}

:root[data-theme="light"] .timeline-date {
  color: rgba(42, 42, 40, 0.5) !important;
}

:root[data-theme="light"] .custom-tag {
  background-color: #e0e0e0 !important;
  color: #2a2a28 !important;
}

:root[data-theme="light"] .custom-tag:hover {
  background-color: #d0d0d0 !important;
}

:root[data-theme="light"] .custom-tag-input {
  background: #f5f5f5 !important;
  border: 1px solid rgba(42, 42, 40, 0.2) !important;
  color: #2a2a28 !important;
}

:root[data-theme="light"] .tags-toggle {
  background: #f5f5f5 !important;
  border: 1px solid rgba(42, 42, 40, 0.2) !important;
  color: #2a2a28 !important;
}

/* Dark theme - keep original white text */
:root[data-theme="dark"] .candidate-name {
  color: #ffffff !important;
}

:root[data-theme="dark"] .candidate-title {
  color: rgba(255, 255, 255, 0.85) !important;
}

:root[data-theme="dark"] .at {
  color: rgba(250, 249, 246, 0.694) !important;
}

:root[data-theme="dark"] .jobby {
  color: rgba(255, 255, 255, 0.6) !important;
}

:root[data-theme="dark"] .location {
  color: rgba(255, 255, 255, 0.5) !important;
}

:root[data-theme="dark"] .candidate-id, 
:root[data-theme="dark"] .indexed-date {
  color: rgba(250, 249, 246, 0.6) !important;
}

:root[data-theme="dark"] .match-text {
  color: rgba(250, 249, 246, 0.9) !important;
}

:root[data-theme="dark"] .match-type-heading {
  color: rgba(250, 249, 246, 0.9) !important;
}

:root[data-theme="dark"] .match-type, 
:root[data-theme="dark"] .match-context {
  color: rgba(250, 249, 246, 0.6) !important;
}

:root[data-theme="dark"] .tag-group-title {
  color: #e0e0e0 !important;
}

:root[data-theme="dark"] .section-label {
  color: #ccc !important;
}

:root[data-theme="dark"] .timeline-agent {
  color: #ccc !important;
}

:root[data-theme="dark"] .timeline-date {
  color: #888 !important;
}

:root[data-theme="dark"] .custom-tag {
  background-color: #555 !important;
  color: #fff !important;
}

:root[data-theme="dark"] .custom-tag:hover {
  background-color: #666 !important;
}

:root[data-theme="dark"] .custom-tag-input {
  background: #333 !important;
  border: 1px solid #555 !important;
  color: white !important;
}

:root[data-theme="dark"] .tags-toggle {
  background: #333 !important;
  border: 1px solid #555 !important;
  color: #ccc !important;
}


/* Exception for elements that should keep their specific colors - light theme only */
:root[data-theme="light"] .tag-btn, 
:root[data-theme="light"] .custom-tag,
:root[data-theme="light"] button,
:root[data-theme="light"] .action-button,
:root[data-theme="light"] [class*="tag-"],
:root[data-theme="light"] .nav-link,
:root[data-theme="light"] .sign-out-btn {
  color: inherit !important;
}

/* Fix placeholder text for both themes */
input::placeholder,
textarea::placeholder,
select::placeholder {
  color: var(--text-color) !important;
  opacity: 0.6;
}

/* ADD after .sign-out-btn styles: */

/* Theme Toggle Button - Professional Style */
.theme-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-color);
  color: var(--text-color);
  border: 2px solid var(--border-color);
  padding: 0.5rem;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 40px;
  height: 40px;
  position: relative;
  overflow: hidden;
}

.theme-toggle-btn:hover {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
  transform: rotate(180deg);
}

.theme-toggle-btn i {
  font-size: 1rem;
  transition: all 0.3s ease;
}

/* Light theme specific styling */
:root[data-theme="light"] .theme-toggle-btn {
  background: white;
  border-color: #fca5a5;
  color: #991b1b;
}

:root[data-theme="light"] .theme-toggle-btn:hover {
  background: var(--primary-color);
  color: white;
  box-shadow: 0 4px 12px rgba(212, 47, 61, 0.25);
}

/* Dark theme specific styling */
:root[data-theme="dark"] .theme-toggle-btn {
  background: rgba(212, 47, 61, 0.1);
  border-color: rgba(212, 47, 61, 0.3);
}

:root[data-theme="dark"] .theme-toggle-btn:hover {
  background: var(--primary-color);
  color: white;
  box-shadow: 0 4px 12px rgba(212, 47, 61, 0.4);
}

/* Animation when theme changes */
.theme-toggle-btn:active {
  transform: scale(0.9);
}
</style>